export type Currency = 'BYN' | 'RUB' | 'USD';
export type CalculationType = 'sales' | 'productivity';

export const CURRENCY_LABELS: Record<Currency, string> = {
  BYN: 'BYN — белорусский рубль',
  RUB: 'RUB — российский рубль',
  USD: 'USD — доллар США',
};

export const CURRENCY_CODES: Record<Currency, string> = {
  BYN: 'BYN',
  RUB: 'RUB',
  USD: 'USD',
};

export const CURRENCY_SYMBOLS: Record<Currency, string> = {
  BYN: 'Br',
  RUB: '₽',
  USD: '$',
};

export interface RoiInput {
  trainingCost: number;
  extraCosts: number;
  participants: number;
  monthlyRevenue: number;
  revenueGrowthPct: number;
  marginPct: number;
  payroll: number;
  productivityGrowthPct: number;
  influencePct: number;
  periodMonths: number;
}

export interface Scenario {
  label: string;
  effect: number;
  netEffect: number;
  roiPct: number;
}

export interface RoiResult {
  totalInvestment: number;
  financialEffect: number;
  netEffect: number;
  roiPct: number;
  breakevenPct: number;
  returnPerUnit: number;
  scenarios: Scenario[];
}

function buildScenarios(financialEffect: number, totalInvestment: number): Scenario[] {
  const build = (label: string, effect: number): Scenario => {
    const netEffect = effect - totalInvestment;
    const roiPct = totalInvestment > 0 ? (netEffect / totalInvestment) * 100 : 0;
    return { label, effect, netEffect, roiPct };
  };
  return [
    build('Консервативный', financialEffect * 0.5),
    build('Базовый', financialEffect * 1),
    build('Оптимистичный', financialEffect * 1.5),
  ];
}

export function calculateRoi(type: CalculationType, input: RoiInput): RoiResult {
  const totalInvestment = input.trainingCost + input.extraCosts;
  let financialEffect = 0;
  let breakevenPct = 0;

  if (type === 'sales') {
    const additionalRevenue =
      input.monthlyRevenue * (input.revenueGrowthPct / 100) * input.periodMonths;
    const additionalMargin = additionalRevenue * (input.marginPct / 100);
    financialEffect = additionalMargin * (input.influencePct / 100);

    const denominator =
      input.monthlyRevenue *
      input.periodMonths *
      (input.marginPct / 100) *
      (input.influencePct / 100);
    breakevenPct = denominator > 0 ? (totalInvestment / denominator) * 100 : 0;
  } else {
    const productivityEffect =
      input.payroll * (input.productivityGrowthPct / 100) * input.periodMonths;
    financialEffect = productivityEffect * (input.influencePct / 100);

    const denominator =
      input.payroll * input.periodMonths * (input.influencePct / 100);
    breakevenPct = denominator > 0 ? (totalInvestment / denominator) * 100 : 0;
  }

  const netEffect = financialEffect - totalInvestment;
  const roiPct =
    totalInvestment > 0 ? ((financialEffect - totalInvestment) / totalInvestment) * 100 : 0;
  const returnPerUnit = totalInvestment > 0 ? financialEffect / totalInvestment : 0;

  return {
    totalInvestment,
    financialEffect,
    netEffect,
    roiPct,
    breakevenPct,
    returnPerUnit,
    scenarios: buildScenarios(financialEffect, totalInvestment),
  };
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(value);
}

export function formatMoney(value: number, currency: Currency): string {
  return `${formatNumber(value)} ${CURRENCY_SYMBOLS[currency]}`;
}

export function formatSignedMoney(value: number, currency: Currency): string {
  if (value > 0) return `+${formatMoney(value, currency)}`;
  if (value < 0) return `−${formatMoney(Math.abs(value), currency)}`;
  return formatMoney(0, currency);
}

export function formatPercent(value: number): string {
  return `${formatNumber(value)}%`;
}

export function formatMultiplier(value: number): string {
  return `${formatNumber(value)} ×`;
}